"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { X, Sparkles } from "lucide-react";

import { ViewMode } from "@/types/thread";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  createRoom,
  getNewGenerations,
  setShowSignupModal,
  cancelActiveJob,
} from "@/redux/actions";
import { useGenContext } from "@/context/GenContext";
import useDeepCompareEffect from "@/hooks/useDeepCompareEffect";
import DailyGensSummaryCompact from "../MobileLayout/DailyGensSummaryCompact";
import DailyGensSummary from "../MobileLayout/DailyGensSummary";
import ModelSelect from "./ModelSelect";
import AspectRatioSelect from "./AspectRatioSelect";
import TotalImagesSelect from "./TotalImagesSelect";
import AdvancedSettings from "./AdvancedSettings";
import PromptInputArea from "./PromptInputArea";
import PreviewOverview from "./PreviewOverview";
import TypingSpinner from "@/components/common/TypingSpinner";

interface MobileGenInputsProps {
  showHeading?: boolean;
  existingRoomUUID?: string;
  onClose?: () => void;
  collapsed?: boolean;
  onCollapse?: () => void;
  showCompactSummary?: boolean;
  initialConfig?: {
    prompt?: string;
    images?: File[];
    selectedModes?: {
      isPublic: boolean;
      is18Plus: boolean;
      isTextImage: boolean;
    };
    aspectRatio?: string;
    selectedModel?: string;
    totalImages?: number;
  };
}

const MobileGenInputs: React.FC<MobileGenInputsProps> = ({
  showHeading = true,
  existingRoomUUID,
  onClose,
  collapsed = false,
  onCollapse = () => {},
  showCompactSummary = false,
  initialConfig,
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    state: { prompt: genPrompt },
    dispatch: genDispatch,
  } = useGenContext();
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const authLoading = useAppSelector((state) => state.user.authLoading);
  const quotas = useAppSelector((state) => state.user.quotas);
  const jobId =
    user?.activeJobs && user.activeJobs.length > 0
      ? user.activeJobs[0].id
      : null;
  const hasActiveJobs = Boolean(jobId);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dailyGensSummaryRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [images, setImages] = useState<File[]>(initialConfig?.images || []);
  const [selectedModes, setSelectedModes] = useState<{
    isPublic: boolean;
    is18Plus: boolean;
    isTextImage: boolean;
  }>(
    initialConfig?.selectedModes || {
      isPublic: true,
      is18Plus: false,
      isTextImage: false,
    }
  );
  const [aspectRatio, setAspectRatio] = useState(
    initialConfig?.aspectRatio || "1:1"
  );
  const [selectedModel, setSelectedModel] = useState("auto");
  const [totalImages, setTotalImages] = useState(
    initialConfig?.totalImages || 4
  );

  const isGenerating = isSubmitting || hasActiveJobs;
  const quota = quotas.find((q) => q.resourceName === "gen");
  const hasUsedQuota = quota && quota.currentQuota < quota.baseQuota;
  const hideControls = Boolean(isGenerating && existingRoomUUID);

  const handleAspectRatioChange = (newAspectRatio: string) => {
    setAspectRatio(newAspectRatio);
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
  };

  const handleModeChange = (type: string, value: boolean) => {
    setSelectedModes((prevModes) => ({
      ...prevModes,
      [type]: value,
    }));
  };

  const handlePromptChange = (newPrompt: string) => {
    genDispatch({ type: "SET_PROMPT", payload: newPrompt });
  };

  const handleImagesChange = (newImages: File[]) => {
    setImages(newImages);
    genDispatch({ type: "SET_IMAGES", payload: newImages });
  };

  const handleCreate = async () => {
    const prompt = genPrompt?.trim();
    if (!prompt || isGenerating) return;

    setIsSubmitting(true);

    try {
      if (!isAuthenticated && !authLoading) {
        const generationDraft = {
          prompt,
          images: images.map((file) => URL.createObjectURL(file)),
          imageMask: null,
          aspectRatio,
          model: selectedModel,
          numImages: totalImages,
          viewMode: selectedModes.isPublic ? "public" : "private",
          timestamp: Date.now(),
        };
        localStorage.setItem(
          "generationDraft",
          JSON.stringify(generationDraft)
        );
        dispatch(setShowSignupModal(true));
        return;
      }

      const viewMode: ViewMode = selectedModes.isPublic
        ? ViewMode.Public
        : ViewMode.Private;

      if (existingRoomUUID) {
        await dispatch(
          getNewGenerations({
            prompt,
            inspiration: images,
            model: selectedModel,
            numImages: totalImages,
            defaultAspectRatio: aspectRatio,
            viewMode,
          })
        ).unwrap();

        genDispatch({ type: "SET_PROMPT", payload: "" });
        genDispatch({ type: "SET_IMAGES", payload: [] });
        setImages([]);
        onCollapse();
      } else {
        const ideaUUID = await dispatch(
          createRoom({
            prompt,
            inspiration: images,
            model: selectedModel,
            numImages: totalImages,
            defaultAspectRatio: aspectRatio,
            viewMode,
          })
        ).unwrap();

        if (ideaUUID) {
          router.push(`/idea/${ideaUUID}`);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateClick = async () => {
    if (isGenerating && jobId) {
      dispatch(cancelActiveJob(jobId));
      setIsSubmitting(false);
    } else {
      handleCreate();
    }
  };

  useDeepCompareEffect(() => {
    if (initialConfig) {
      setImages(initialConfig.images || []);
      setSelectedModes(
        initialConfig.selectedModes || {
          isPublic: true,
          is18Plus: false,
          isTextImage: false,
        }
      );
      setAspectRatio(initialConfig.aspectRatio || "1:1");
      setSelectedModel(
        initialConfig.selectedModel &&
          initialConfig.selectedModel !== "hd-turbo"
          ? initialConfig.selectedModel
          : "auto"
      );
      setTotalImages(initialConfig.totalImages || 4);
    }
  }, [initialConfig]);

  return (
    <>
      {showCompactSummary && !isSubscribed && hasUsedQuota && (
        <div className="tw:fixed tw:top-[50px] tw:left-0 tw:right-0 tw:z-[1000] tw:bg-white tw:shadow-[0_2px_8px_rgba(0,0,0,0.1)] tw:animate-[slideDown_0.3s_ease-out]">
          <DailyGensSummaryCompact />
        </div>
      )}
      <div
        className={cn(
          "tw:relative tw:text-foreground-primary tw:font-sans tw:pt-2",
          onClose && "tw:pt-0"
        )}
        ref={containerRef}
      >
        {onClose && (
          <div className="tw:flex tw:justify-end tw:w-full tw:sticky tw:top-0 tw:left-0 tw:z-[1] tw:bg-transparent tw:p-2">
            <X onClick={onClose} />
          </div>
        )}
        <div
          className={cn(
            "tw:w-full tw:flex tw:flex-col tw:gap-4 tw:py-2 tw:px-5",
            existingRoomUUID && "tw:px-0"
          )}
        >
          {!collapsed && !existingRoomUUID && !isSubscribed && hasUsedQuota && (
            <div ref={dailyGensSummaryRef}>
              <DailyGensSummary />
            </div>
          )}
          {showHeading && (
            <h1 className="tw:text-[1.375rem] tw:font-medium tw:mb-2 tw:pb-4 tw:border-b tw:border-b-neutral-200">
              Create Something{" "}
              <span className="tw:text-primary-red">Extraordinary</span>
            </h1>
          )}
          {!hideControls && (
            <div className="tw:flex tw:items-center tw:space-around tw:gap-2.5">
              <ModelSelect
                selectedModes={selectedModes}
                selectedModel={selectedModel}
                hasImages={images.length > 0}
                aspectRatio={aspectRatio}
                setSelectedModel={handleModelChange}
                isSubscribed={isSubscribed}
              />
              <AspectRatioSelect
                selectedModel={selectedModel}
                aspectRatio={aspectRatio}
                setAspectRatio={handleAspectRatioChange}
                isSubscribed={isSubscribed}
              />
              <TotalImagesSelect
                selectedModel={selectedModel}
                totalImages={totalImages}
                setTotalImages={setTotalImages}
                isSubscribed={isSubscribed}
              />
            </div>
          )}

          {!hideControls && (
            <AdvancedSettings
              selectedModel={selectedModel}
              is18Plus={selectedModes.is18Plus}
              isTextImage={selectedModes.isTextImage}
              isPublic={selectedModes.isPublic}
              onModeChange={handleModeChange}
            />
          )}
          <PromptInputArea
            prompt={genPrompt || ""}
            onChange={handlePromptChange}
            onCreate={handleCreate}
            images={images}
            setImages={handleImagesChange}
            isGenerating={isGenerating}
            selectedModel={selectedModel}
          />
          <button
            className="tw:w-full tw:min-h-9 tw:border-none tw:flex tw:items-center tw:justify-center tw:gap-2 tw:p-2 tw:rounded-lg tw:bg-primary-red tw:text-white"
            onClick={handleGenerateClick}
          >
            {isGenerating ? (
              <>
                {hasActiveJobs && (
                  <span className="tw:text-sm tw:font-medium">Cancel</span>
                )}
                <TypingSpinner color="white" />
              </>
            ) : (
              <>
                <Sparkles size={18} />
                <span className="tw:text-sm tw:font-medium">
                  Generate Image
                </span>
                <span className="tw:w-9 tw:h-3.5 tw:bg-white tw:text-black tw:text-xxs tw:font-medium tw:rounded-sm">
                  Free
                </span>
              </>
            )}
          </button>
        </div>
        <PreviewOverview
          selectedModel={selectedModel}
          aspectRatio={aspectRatio}
          contained={Boolean(existingRoomUUID)}
        />
      </div>
    </>
  );
};

export default MobileGenInputs;
